import { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import DefaultImage from '../../../assets/images/DefaultImage.jpeg';
import './MediaGallery.css';

const MediaGallery = ({ images = [], promotion_videos = [], attachments = [] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const allMedia = [
    ...images.map((img) => ({ type: 'image', url: img.url })),
    ...promotion_videos.map((vid) => ({ type: 'video', url: vid.url })),
    ...attachments.map((pdf) => ({ type: 'pdf', url: pdf.url, name: pdf.name })),
  ];

  const totalCount = allMedia.length || 1;

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : allMedia.length - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev < allMedia.length - 1 ? prev + 1 : 0));
  };

  const renderMainMedia = () => {
    const media = allMedia[activeIndex];
    if (!media) {
      return (
        <img
          src={DefaultImage}
          className="saas-gallery-main-media"
          alt="Default Property"
        />
      );
    }

    if (media.type === 'image') {
      return (
        <img
          src={media.url}
          className="saas-gallery-main-media"
          alt={`Property Media ${activeIndex + 1}`}
          onClick={() => setShowModal(true)}
          title="Click to view full size"
        />
      );
    }

    if (media.type === 'video') {
      return (
        <video
          controls
          className="saas-gallery-main-media"
          onClick={() => setShowModal(true)}
        >
          <source src={media.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (media.type === 'pdf') {
      return (
        <div
          onClick={() => window.open(media.url, '_blank')}
          className="d-flex flex-column align-items-center justify-content-center h-100 w-100 bg-light p-4 text-center"
          style={{ cursor: 'pointer' }}
        >
          <span role="img" aria-label="pdf" style={{ fontSize: '2.5rem' }}>📄</span>
          <p className="fw-semibold text-dark mb-1 mt-2">{media.name || 'PDF Document'}</p>
          <small className="text-primary">(Click to view in new tab)</small>
        </div>
      );
    }

    return null;
  };

  const renderModalContent = () => {
    const media = allMedia[activeIndex];
    if (!media) return null;

    if (media.type === 'image') {
      return (
        <img
          src={media.url}
          className="img-fluid rounded"
          style={{ maxHeight: '85vh', objectFit: 'contain' }}
          alt="Enlarged view"
        />
      );
    }

    if (media.type === 'video') {
      return (
        <video controls className="w-100 rounded" style={{ maxHeight: '85vh' }}>
          <source src={media.url} type="video/mp4" />
        </video>
      );
    }

    if (media.type === 'pdf') {
      return (
        <iframe
          src={media.url}
          title="PDF Preview"
          style={{ width: '100%', height: '85vh', border: 'none' }}
        />
      );
    }

    return null;
  };

  // Up to 3 visible preview thumbnails, 4th item can be a "+N" counter if more exist
  const MAX_THUMBS = 3;
  const visibleMedia = allMedia.slice(0, MAX_THUMBS);
  const remainingCount = allMedia.length > MAX_THUMBS ? allMedia.length - MAX_THUMBS : 0;

  return (
    <div className="saas-gallery-root">
      {/* Main Media with Overlays */}
      <div className="saas-gallery-main">
        {renderMainMedia()}

        {/* Counter Badge */}
        {allMedia.length > 0 && (
          <div className="saas-gallery-counter">
            {activeIndex + 1} / {totalCount}
          </div>
        )}

        {/* Prev / Next Navigation overlay buttons */}
        {allMedia.length > 1 && (
          <>
            <button
              type="button"
              className="saas-gallery-nav-btn saas-gallery-nav-btn--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              <FiChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="saas-gallery-nav-btn saas-gallery-nav-btn--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              <FiChevronRight size={22} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails Row */}
      {allMedia.length > 1 && (
        <div className="saas-gallery-thumbs">
          {visibleMedia.map((media, index) => (
            <button
              type="button"
              key={index}
              className={`saas-gallery-thumb-item ${index === activeIndex ? 'is-active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Select media item ${index + 1}`}
            >
              {media.type === 'image' ? (
                <img
                  src={media.url}
                  className="saas-gallery-thumb-img"
                  alt={`Thumb ${index + 1}`}
                />
              ) : media.type === 'video' ? (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-dark text-white">
                  🎬
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light">
                  📄
                </div>
              )}
            </button>
          ))}

          {/* "+N" More tile if more than 3 media items */}
          {remainingCount > 0 && (
            <button
              type="button"
              className={`saas-gallery-thumb-item saas-gallery-thumb-more ${
                activeIndex >= MAX_THUMBS ? 'is-active' : ''
              }`}
              onClick={() => {
                if (activeIndex >= MAX_THUMBS) {
                  // Cycle to next remaining or open modal
                  setActiveIndex((prev) => (prev + 1 < allMedia.length ? prev + 1 : MAX_THUMBS));
                } else {
                  setActiveIndex(MAX_THUMBS);
                }
              }}
              aria-label={`View ${remainingCount} more media items`}
            >
              +{remainingCount}
            </button>
          )}
        </div>
      )}

      {/* Modal Lightbox Viewer */}
      {showModal && (
        <div
          className="modal show fade"
          style={{ display: 'block', backgroundColor: 'rgba(15, 23, 42, 0.92)' }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content bg-transparent border-0">
              <div className="modal-body text-center position-relative p-0">
                <button
                  type="button"
                  className="saas-modal-close-btn"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                >
                  <FiX size={20} />
                </button>
                {renderModalContent()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
